import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { Payment } from "@/components/settings/admin/stats/payments/types";
import { usePaymentMethods } from "@/hooks/payments/usePaymentMethods";
import { PaymentMethodSelector } from "@/components/settings/admin/stats/payments/form/PaymentMethodSelector";
import { PaymentMethodManager } from "@/components/settings/admin/stats/payments/form/PaymentMethodManager";
import { getPaymentDate } from "@/lib/paymentDate";

interface EditPaymentDialogProps {
  payment: Payment | null;
  gymId?: string | null;
  onClose: () => void;
}

export const EditPaymentDialog = ({ payment, gymId, onClose }: EditPaymentDialogProps) => {
  const queryClient = useQueryClient();
  const { methods, isLoading: methodsLoading } = usePaymentMethods(gymId ?? null);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState<string>("");
  const [methodId, setMethodId] = useState<string | null>(null);
  const [managerOpen, setManagerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (payment) {
      setDate(getPaymentDate(payment));
      setDescription(payment.description || "");
      setAmount(String(payment.amount ?? ""));
      const matched = methods.find((m) => m.name === payment.payment_method);
      setMethodId(matched?.id ?? null);
    }
  }, [payment, methods]);

  const open = !!payment;

  const handleSave = async () => {
    if (!payment) return;
    if (!date) {
      toast({ variant: "destructive", title: "Falta la fecha", description: "Seleccioná una fecha." });
      return;
    }
    const numericAmount = Number(amount);
    if (Number.isNaN(numericAmount) || numericAmount <= 0) {
      toast({ variant: "destructive", title: "Monto inválido", description: "Ingresá un monto válido." });
      return;
    }
    setSaving(true);
    try {
      const selectedMethod = methods.find((m) => m.id === methodId);
      const { error } = await supabase
        .from("payments")
        .update({
          paid_at: date.toISOString(),
          description: description || null,
          amount: numericAmount,
          payment_method: selectedMethod?.name ?? null,
        })
        .eq("id", payment.id);
      if (error) throw error;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["payments", gymId] }),
        queryClient.invalidateQueries({ queryKey: ["payments"] }),
        queryClient.invalidateQueries({ queryKey: ["lastPayment"] }),
      ]);
      toast({ title: "Pago actualizado", description: "Los cambios se guardaron correctamente." });
      onClose();
    } catch (e: any) {
      console.error("Error updating payment:", e);
      toast({ variant: "destructive", title: "Error", description: "No se pudo actualizar el pago." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar pago</DialogTitle>
            <DialogDescription>Modificá los datos del pago seleccionado.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Fecha</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP", { locale: es }) : <span>Elegí una fecha</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-black/70 backdrop-blur-md" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    initialFocus
                    locale={es}
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">Descripción</Label>
              <Input
                id="edit-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ej: Mensualidad, Inscripción"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-amount">Monto</Label>
              <Input
                id="edit-amount"
                type="number"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
              />
            </div>

            <PaymentMethodSelector
              methods={methods}
              selectedId={methodId}
              onSelect={setMethodId}
              onManageClick={() => setManagerOpen(true)}
              isLoading={methodsLoading}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Guardando..." : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {gymId && (
        <Dialog open={managerOpen} onOpenChange={setManagerOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Métodos de pago</DialogTitle>
            </DialogHeader>
            <PaymentMethodManager gymId={gymId} onBack={() => setManagerOpen(false)} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
